import * as THREE from 'three'
import type { Beat, Character } from '../data/cutscenes'
import { storyCourierPosition, storyBarrelPosition, storyGiantGuardMouth, storyShipBagPosition } from './DioramaStoryProps'
import type { CollisionBody, CollisionBounds } from './CollisionGuard'
import { boundsOf } from './CollisionGuard'

export function clearPlacementXZ(moving: CollisionBounds, blockers: CollisionBounds[], padding = 0.055): { x: number; z: number } {
  const obstacles = blockers.filter(blocker => moving.minY < blocker.maxY && moving.maxY > blocker.minY)
  const xs = [0], zs = [0]
  for (const blocker of obstacles) {
    xs.push(blocker.minX - moving.maxX - padding, blocker.maxX - moving.minX + padding)
    zs.push(blocker.minZ - moving.maxZ - padding, blocker.maxZ - moving.minZ + padding)
  }
  let best = { x: 0, z: 0 }, distance = Infinity
  for (const x of xs) for (const z of zs) {
    const squared = x * x + z * z
    if (squared >= distance) continue
    if (obstacles.some(blocker => moving.minX + x < blocker.maxX && moving.maxX + x > blocker.minX
      && moving.minZ + z < blocker.maxZ && moving.maxZ + z > blocker.minZ)) continue
    best = { x, z }
    distance = squared
  }
  return best
}

type CrowdMode = 'none' | 'theft' | 'signs' | 'bridge' | 'cargo' | 'trap' | 'storm' | 'captives' | 'celebration' | 'courier' | 'web' | 'clam-guard' | 'barrel'

/** Supporting performers are separate from named Zap: violet guards and orange fish witnesses.
 * Shared instanced geometry keeps the entire supporting cast within ten draw calls. */
export class DioramaExtras {
  readonly root = new THREE.Group()
  private beat: Beat | null = null
  private mode: CrowdMode = 'none'
  private fish = false
  private readonly transform = new THREE.Object3D()
  private readonly color = new THREE.Color()
  private readonly bodies: THREE.InstancedMesh
  private readonly whites: THREE.InstancedMesh
  private readonly mouths: THREE.InstancedMesh
  private readonly pupils: THREE.InstancedMesh
  private readonly limbs: THREE.InstancedMesh
  private readonly fins: THREE.InstancedMesh
  private readonly bubbles: THREE.InstancedMesh
  private readonly signs: THREE.InstancedMesh
  private readonly axis = new THREE.Vector3(0, 1, 0)
  private readonly direction = new THREE.Vector3()
  private readonly beams: THREE.InstancedMesh
  private readonly pools: THREE.InstancedMesh[] = []
  private blockers: CollisionBody[] = []
  private occupied: CollisionBounds[] = []
  private auditIndex = 0
  private readonly auditObjects: THREE.Object3D[] = []
  private readonly auditBodies: CollisionBody[] = []

  constructor() {
    this.root.name = 'StoryWitnessesAndGuards'
    const sphere = new THREE.SphereGeometry(1, 12, 8)
    const material = (color: number, roughness = 0.48, metalness = 0) => new THREE.MeshStandardMaterial({ color, roughness, metalness })
    const pool = (name: string, geometry: THREE.BufferGeometry, mat: THREE.Material, capacity: number) => {
      const mesh = new THREE.InstancedMesh(geometry, mat, capacity)
      mesh.name = name; mesh.count = 0; mesh.castShadow = true; mesh.receiveShadow = true
      mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
      mesh.frustumCulled = false
      this.pools.push(mesh); this.root.add(mesh)
      return mesh
    }
    this.bodies = pool('distinct-guard-and-witness-bodies', sphere, material(0xffffff, 0.3), 12)
    this.whites = pool('supporting-cast-eyes', sphere, material(0xfffff2), 24)
    this.mouths = pool('supporting-guard-mouths', sphere, material(0x29172e), 12)
    this.pupils = pool('supporting-cast-pupils', sphere, material(0x112138), 24)
    this.limbs = pool('guard-tentacles', sphere, material(0xb84aff, 0.4), 64)
    this.fins = pool('fish-fins-and-tails', new THREE.ConeGeometry(1, 1, 3), material(0xffa13d), 16)
    this.bubbles = pool('guarded-bridge-bubbles', sphere, new THREE.MeshPhysicalMaterial({ color: 0x95dffe, transparent: true, opacity: 0.23, roughness: 0.08, metalness: 0.12, clearcoat: 1, depthWrite: false }), 3)
    this.signs = pool('guard-rotated-maze-signs', new THREE.BoxGeometry(1, 1, 1), material(0xceb178, 0.8), 6)
    this.beams = pool('visible-command-and-zap-links', new THREE.CylinderGeometry(1, 1, 1, 5), new THREE.MeshBasicMaterial({ color: 0xb26dff, transparent: true, opacity: 0.8 }), 12)
    this.bodies.userData.storyPurpose = 'named witnesses and freed guards'
    this.limbs.userData.storyPurpose = 'supporting guard gesture limbs'
    this.fins.userData.storyPurpose = 'seaweed witnesses pointing to the coin trail'
    this.bubbles.userData.storyPurpose = 'bridge route markers'
    this.signs.userData.storyPurpose = 'seaweed route signs'
    this.beams.userData.storyPurpose = 'active command or web links'
  }

  setBeat(beat: Beat): void {
    this.beat = beat
    const shot = beat.shot
    this.mode = ['t2-2a-02', 't3-1b-01', 't4-1a-03', 't6-1c-01', 't7-3-01'].includes(shot) ? 'courier'
      : /^t5-2-0[23]$/.test(shot) ? 'barrel'
      : shot === 'intro-3d' ? 'theft'
      : beat.scene === 'coralcave' ? 'web'
      : shot === 't6-2b-02' ? 'clam-guard'
      : beat.scene === 'seaweed' ? 'signs'
      : beat.scene === 'bubblebridge' && !shot.startsWith('t5-1b') && !shot.startsWith('t5-1c') ? 'bridge'
      : beat.scene === 'ship' ? 'cargo'
      : beat.scene === 'pattyreef' ? (shot === 't8-1c' ? 'celebration' : 'trap')
      : beat.scene === 'storm' ? 'storm'
      : beat.scene === 'royalcave' || beat.scene === 'throne' ? 'captives'
      : beat.scene === 'reopening' || shot === 'ending-3a' ? 'celebration' : 'none'
    this.fish = beat.scene === 'seaweed' || beat.scene === 'reopening'
    this.root.visible = this.mode !== 'none' || this.fish
    this.update(0, 0)
  }

  private place(mesh: THREE.InstancedMesh, x: number, y: number, z: number, sx: number, sy: number, sz: number, rz = 0, color?: number): void {
    const index = mesh.count++
    this.transform.position.set(x, y, z); this.transform.rotation.set(0, 0, rz); this.transform.scale.set(sx, sy, sz)
    this.transform.updateMatrix(); mesh.setMatrixAt(index, this.transform.matrix)
    if (color !== undefined) mesh.setColorAt(index, this.color.setHex(color))
  }

  private link(x: number, y: number, z: number, tx: number, ty: number, tz: number, thickness = 0.025): void {
    const dx = tx - x, dy = ty - y, dz = tz - z
    const length = Math.hypot(dx, dy, dz)
    if (length < 0.01) return
    const index = this.beams.count++
    this.transform.position.set((x + tx) / 2, (y + ty) / 2, (z + tz) / 2)
    this.transform.quaternion.setFromUnitVectors(this.axis, this.direction.set(dx / length, dy / length, dz / length))
    this.transform.scale.set(thickness, length, thickness); this.transform.updateMatrix()
    this.beams.setMatrixAt(index, this.transform.matrix)
  }

  update(progress: number, elapsed: number, positionOf?: (character: Character) => THREE.Vector3, blockers: CollisionBody[] = []): void {
    if (!this.beat) return
    this.blockers = blockers
    this.occupied = []
    this.auditIndex = 0
    this.auditBodies.length = 0
    for (const pool of this.pools) pool.count = 0
    const p = THREE.MathUtils.clamp(progress, 0, 1)
    const shot = this.beat.shot
    const sceneFreed = this.mode === 'celebration'
      || shot.startsWith('ending-') || shot.startsWith('t10-')
      || /^t6-(1c|tilt)/.test(shot)
      || (this.mode === 'courier' && (shot !== 't2-2a-02' || p >= 0.3))
      || (shot === 't5-2-03' && p > 0.7)
      || (shot === 't8-1b' && p >= 0.55)
      || (shot === 't4-2b-04' && p >= 0.56)
    const guards = shot === 't5-1b' ? 2 : this.mode === 'none' ? 0 : this.mode === 'courier' || this.mode === 'clam-guard' || this.mode === 'barrel' ? 1 : this.mode === 'theft' ? 3 : this.mode === 'web' ? 2 : this.mode === 'captives' ? 8 : this.mode === 'trap' ? 6 : 3
    for (let i = 0; i < guards; i++) {
      const freed = sceneFreed && (shot !== 't4-2b-04' || i === 0)
      const theta = (i / guards) * Math.PI * 2 + elapsed * (freed ? 0.28 : 0.055)
      let x = -3.3 + i * 2.7, y = 2.7 + Math.sin(elapsed * 2 + i) * 0.13, z = -1.5
      if (this.mode === 'trap' || this.mode === 'captives') {
        const radius = this.mode === 'trap' ? 4.8 - p * 1.4 : 4.4
        x = Math.cos(theta) * radius; z = Math.sin(theta) * 1.6 - 1.6; y = 2.6 + (i % 2) * 0.75
        if (this.mode === 'captives') { z = Math.sin(theta) * 0.65 - 3.8; y = 3.7 + (i % 2) * 0.75 }
      }
      if (this.mode === 'theft') {
        const eased = p * p * (3 - 2 * p)
        if (i < 2) { x = 1.5 + 4.5 * eased + (i ? 0.9 : -0.9); y = 3.3 + 2.8 * eased; z = -1.8 - 3.6 * eased }
        else if (i === 2) { x = 3.5 + 3.5 * eased; y = 3.3 + 2.6 * eased; z = -1.8 - 3.5 * eased }
        else {
          const bag = i - 3
          // Four background carriers need distinct lanes at the reveal. The
          // old formula put carrier 0 on the exact same path as guard 0.
          x = -2.4 + bag * 1.65 + eased * (5 + bag * 0.5)
          y = 1.22 + eased * (2.7 + bag * 0.3)
          z = 1.2 - eased * 4.8
        }
      }
      if (this.mode === 'courier') {
        const bob = positionOf?.('bluebob') ?? new THREE.Vector3(-1.4, 0, 0)
        const cargo = storyCourierPosition(shot, p, bob)
        x = cargo.x; y = cargo.y + 1.1; z = cargo.z
      }
      if (this.mode === 'barrel') {
        const barrel = storyBarrelPosition()
        const emerge = shot === 't5-2-03' ? p * p * (3 - 2 * p) : 0
        x = barrel.x - emerge * 0.25; y = barrel.y + 1.1 + emerge * 0.6; z = barrel.z
      }
      if (this.mode === 'web') { x = i ? 3.3 : -3.3; y = 2.7; z = -2.2 }
      if (this.mode === 'clam-guard') { x = 0; y = 2; z = -1 }
      if (this.mode === 'cargo') { x = -3 + i * 2.4; y = 1.5 + Math.sin(elapsed * 3 + i) * 0.12; z = -0.3 }
      if (shot === 't5-1b') {
        const cargo = storyShipBagPosition(p)
        x = cargo.x + (i ? .55 : -.55); y = cargo.y + 1.1; z = cargo.z
      }
      if (this.mode === 'signs') { x = -3.9 + i * 3.6; z = -2.4; y = 1.7 }
      if (this.mode === 'storm') { x += Math.sin(elapsed * 2 + i) * 0.8; y += 1.2 }
      // Food contact marks must not drift with decorative swimming bob.
      if (freed && !['courier','barrel','bridge'].includes(this.mode)) y += Math.sin(elapsed * 4 + i) * 0.27
      if (shot === 't8-1b') {
        const bob = positionOf?.('bluebob') ?? new THREE.Vector3(-1.4,0,0)
        const mouth = storyGiantGuardMouth(i,p,bob)
        const approach = Math.min(1,p/.3)
        const retreat = Math.max(0,(p-.75)/.25)
        const contact = approach * (1-retreat)
        x = THREE.MathUtils.lerp(x,mouth.x,contact)
        y = THREE.MathUtils.lerp(y,mouth.y+.15,contact)
        z = THREE.MathUtils.lerp(z,mouth.z-.44,contact)
      }
      // Supporting performers have a marked story role. Keep them out of the
      // named actors' collision envelopes unless this shot explicitly stages a
      // handoff, bite, or rescue contact at that point.
      const directContact = ['t2-2a-02', 't4-2b-04', 't5-1b', 't5-2-03', 't8-1b'].includes(shot)
      const tint = freed ? 0xffa7c8 : 0xb449e4
      const resolved = this.placeAudited(this.bodies, x, y, z, 0.53, 0.41, 0.44, Math.sin(elapsed + i) * 0.09, tint, directContact, 'supporting guard or witness body')
      x = resolved.x; y = resolved.y; z = resolved.z
      const chewing = (shot === 't8-1b' && p>=.3 && p<=.75) || (['t2-2a-02','t5-2-03','t4-2b-04'].includes(shot) && p>.2 && p<.75)
      this.place(this.mouths,x,y-.15,z+.44,.14,chewing ? .08+Math.abs(Math.sin(elapsed*12))*.065 : .035,.026)
      for (let side = -1; side <= 1; side += 2) {
        this.place(this.whites, x + side * 0.18, y + 0.02, z + 0.38, 0.14, 0.16, 0.06)
        this.place(this.pupils, x + side * 0.18, y + (freed ? 0.02 : -0.02), z + 0.44, 0.062, freed ? 0.07 : 0.045, 0.035)
      }
      for (let leg = 0; leg < 5; leg++) {
        const sway = Math.sin(elapsed * (freed ? 4 : 2) + leg + i) * 0.12
        if (shot === 't5-1b' && leg === (i ? 0 : 4)) {
          this.place(this.limbs,x + (i ? -.28 : .28),y-.75,z,.075,.6,.075,i ? -.45 : .45)
          continue
        }
        if (this.mode === 'barrel' && leg === 0) {
          const reach = shot === 't5-2-03' ? Math.sin(p * Math.PI) : 0.1
          this.place(this.limbs, x - 0.4 - reach * 0.45, y - 0.2, z + 0.4, 0.065, 0.45, 0.065, -0.7 - reach * 0.6)
          continue
        }
        this.place(this.limbs, x + (leg - 2) * 0.18 + sway, y - 0.56, z, 0.057, 0.42, 0.06, sway)
      }
      if (this.mode === 'signs' && i < 2) {
        const angle = /t3-2a|t4-1a-01/.test(shot) ? Math.sin(p * Math.PI) * 0.9 : 0
        this.placeAudited(this.signs, x, 0.65, z + 0.3, 0.09, 1.3, 0.09, 0, undefined, true, 'seaweed route sign post')
        this.placeAudited(this.signs, x, 1.2, z + 0.35, 0.95, 0.25, 0.1, angle, undefined, true, 'seaweed route sign')
      }
      if (this.mode === 'bridge') {
        const pop = /t4-2a-02|t4-2b-01/.test(shot) && p >= 0.44
        if (shot === 't4-2a-02') {
          // The actual route bubble belongs to DioramaSet; never pop a duplicate
          // elsewhere while the platform beneath BlueBob remains intact.
          if (i === 0 && p >= 0.44 && p < 0.6) this.link(x, y - 0.5, z, -1, 0, 0, 0.045)
        } else if (!pop) this.placeAudited(this.bubbles, x, 0.7, 0.2, 0.66, 0.48, 0.66, 0, undefined, false, 'bridge route bubble')
        else if (p < 0.6) this.link(x, y - 0.5, z, x, 0.7, 0.2, 0.045)
      }
      if (this.mode === 'web') this.link(x, y - 0.4, z, -x, y - 1, z, 0.018)
      if ((this.mode === 'storm' || this.mode === 'captives') && !freed) {
        this.link(x, y + 0.1, z, 0, 4.9, -3.5, 0.014 + Math.max(0, Math.sin(elapsed * 8)) * 0.015)
      }
    }
    if (this.fish) {
      const frightened = /^t3-2b/.test(shot)
      const reveal = frightened ? 0.25 + p * 0.75 : 1
      for (let i = 0; i < 4; i++) {
        let x = -3.0 + i * 0.62, y = 2.9 + reveal * 0.3 + i % 2 * 0.25 + Math.sin(elapsed * 3 + i) * 0.04, z = -2.75
        const scale = 1.05
        const resolved = this.placeAudited(this.bodies, x, y, z, 0.3 * scale, 0.23 * scale, 0.2 * scale, frightened ? -0.12 : 0, i % 2 ? 0xffb14d : 0xff7758, false, 'silent fish witness')
        x = resolved.x; y = resolved.y; z = resolved.z
        this.place(this.fins, x - 0.34 * scale, y, z, 0.25 * scale, 0.3 * scale, 0.1 * scale, Math.PI / 2 + Math.sin(elapsed * 6) * 0.2)
        // The silent witness points toward the true coin trail beneath the seaweed.
        const point = shot === 't3-2b-02' ? -0.6 : 0.35
        this.place(this.fins, x + 0.12 * scale, y - 0.14 * scale, z + 0.15, 0.11, 0.23, 0.08, point)
        this.place(this.whites, x + 0.13 * scale, y + 0.065, z + 0.17, 0.105, 0.115, 0.05)
        this.place(this.pupils, x + 0.15 * scale, y + 0.055, z + 0.215, 0.048, 0.06, 0.025)
      }
    }
    for (const pool of this.pools) {
      pool.instanceMatrix.needsUpdate = true
      if (pool.instanceColor) pool.instanceColor.needsUpdate = true
    }
  }

  /** Purposeful instanced performers and route markers exposed as virtual
   * collision roots for the frame audit. Their facial features, fins, limbs,
   * bubbles used as VFX, and command links remain presentation-only. */
  collisionBodies(): CollisionBody[] {
    return [...this.auditBodies]
  }

  private placeAudited(
    mesh: THREE.InstancedMesh,
    x: number,
    y: number,
    z: number,
    sx: number,
    sy: number,
    sz: number,
    rz = 0,
    color?: number,
    allowContact = false,
    purpose = 'supporting cutscene object',
  ): { x: number; y: number; z: number } {
    const resolved = this.resolveExtraPosition(x, y, z, sx, sy, sz, allowContact)
    this.place(mesh, resolved.x, resolved.y, resolved.z, sx, sy, sz, rz, color)
    const audit = this.auditObjects[this.auditIndex] ?? new THREE.Object3D()
    this.auditObjects[this.auditIndex] = audit
    audit.position.set(resolved.x, resolved.y, resolved.z)
    // The solver uses axis-aligned half extents. Keep the virtual audit proxy
    // axis-aligned as well; applying the decorative z rotation here would make
    // the evidence box larger than the instanced body that was actually placed.
    audit.rotation.set(0, 0, 0)
    audit.scale.set(1, 1, 1)
    audit.userData.collisionProxy = { center: [0, 0, 0], halfSize: [sx, sy, sz] }
    audit.userData.collisionContactAllowed = allowContact
    this.auditBodies.push({
      object: audit,
      name: `Extra_${this.mode}_${this.auditIndex}`,
      kind: 'set-prop',
      purpose,
    })
    this.occupied.push({ minX: resolved.x - sx, maxX: resolved.x + sx, minY: resolved.y - sy, maxY: resolved.y + sy, minZ: resolved.z - sz, maxZ: resolved.z + sz })
    this.auditIndex++
    return resolved
  }

  private resolveExtraPosition(x: number, y: number, z: number, sx: number, sy: number, sz: number, allowContact: boolean): { x: number; y: number; z: number } {
    if (allowContact) return { x, y, z }
    const moving = { minX: x - sx, maxX: x + sx, minY: y - sy, maxY: y + sy, minZ: z - sz, maxZ: z + sz }
    const offset = clearPlacementXZ(moving, this.blockers.map(body => boundsOf(body.object)), 0.04)
    return { x: x + offset.x, y, z: z + offset.z }
  }

  dispose(): void {
    const geometries = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>()
    for (const pool of this.pools) { geometries.add(pool.geometry); materials.add(pool.material as THREE.Material); pool.dispose() }
    geometries.forEach(g => g.dispose()); materials.forEach(m => m.dispose())
    this.root.removeFromParent(); this.root.clear()
  }
}
